/**
 * Print a PDF the browser already holds as bytes.
 *
 * The bytes go into a hidden iframe whose window is asked to print; that works
 * after an `await` where `window.open` would be blocked as a popup, and the
 * browser's PDF viewer does the rendering. If the viewer refuses (some mobile
 * browsers), the PDF opens in a new tab instead so the attorney can print from
 * there.
 */
export const printPdfBytes = (bytes: ArrayBuffer): void => {
  const url = URL.createObjectURL(
    new Blob([bytes], { type: "application/pdf" })
  );
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.tabIndex = -1;
  frame.style.position = "fixed";
  frame.style.right = "0";
  frame.style.bottom = "0";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  frame.src = url;

  const cleanup = () => {
    frame.remove();
    URL.revokeObjectURL(url);
  };

  frame.onload = () => {
    try {
      const win = frame.contentWindow;
      if (!win) throw new Error("no print window");
      // Remove the frame once the dialog closes. `afterprint` is not fired by
      // every viewer, so a long timeout backs it up.
      win.addEventListener("afterprint", cleanup, { once: true });
      win.focus();
      win.print();
      window.setTimeout(cleanup, 60_000);
    } catch {
      window.open(url, "_blank", "noopener");
      // The new tab has the bytes once it loads; give it a moment before the
      // object URL goes away.
      window.setTimeout(cleanup, 10_000);
    }
  };

  document.body.appendChild(frame);
};
